/**
 * Tracking the customer's answer to the opening question ("what drew you to X to
 * begin with?") — the motivation that brought them to the product.
 *
 * WHY THIS EXISTS SEPARATELY FROM `why_bought`:
 *
 * `why_bought` is written by the MODEL, from its `record_intake` tool call. That
 * makes it a paraphrase of what the customer said, and it only exists at all if the
 * model gets as far as calling the tool. Two consequences we had to live with:
 *
 *  1. Abandon the chat before the model records intake and the answer is captured
 *     NOWHERE. The richest churn signal in the flow, gone.
 *  2. You cannot tell a TAPPED chip from TYPED text. A chip's onClick sends the
 *     label exactly as if it had been written, so by the time it reaches the model
 *     the two are indistinguishable — and what lands in `why_bought` is the model's
 *     rendering of it either way. Tap-rate was unmeasurable.
 *
 * So we record the answer ourselves, verbatim, the instant it is sent, and we carry
 * the source with it. This is the ground truth; `why_bought` remains the model's
 * reading of it. Keeping both lets you check the model against the customer.
 *
 * The source MUST come from the call site (which knows whether a chip was tapped),
 * never from matching the text against the chip list: a customer who types "Glowing
 * skin" by hand typed it, and counting that as a tap would quietly inflate the
 * tap-rate this exists to measure.
 */

/** How the customer gave their answer. */
export type MotivationSource = "chip" | "typed";

export interface MotivationAnswer {
  /** Verbatim text sent — the chip's label, or exactly what they typed. */
  text: string;
  source: MotivationSource;
  /** Position of the tapped chip in the list shown; null when typed. */
  chipIndex: number | null;
  /** Every chip on offer at that moment, so a tap-rate has a denominator. */
  chipsShown: string[];
}

/** Analytics properties for the opening motivation answer. */
export function motivationEventProps(
  a: MotivationAnswer,
): Record<string, string | number | undefined> {
  return {
    why_bought_raw: a.text.trim(),
    why_bought_source: a.source,
    // Omitted rather than sent as -1/null for typed answers: a numeric property
    // that is present only for taps is far easier to aggregate over.
    ...(a.source === "chip" && a.chipIndex != null
      ? { why_bought_chip_index: a.chipIndex }
      : {}),
    motivation_chips_shown: a.chipsShown.length,
  };
}
